import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { Link } from 'react-router-dom';
import { userService } from '../services/userService';

const ProfileCard = ({ profile }) => {
  if (!profile) {
    return (
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              My Profile
            </Typography>
          </Box>
          <Chip label="No Profile" color="warning" size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary">
          You don't have a profile yet. 
          <Link to="/profile/edit" style={{ marginLeft: '4px', color: 'inherit' }}>
            Create your profile
          </Link>
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            My Profile
          </Typography>
        </Box>
        <Chip label="Profile Complete" color="success" size="small" />
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          {profile.fullName}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {profile.location}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <Typography variant="body2">
          <strong>Email:</strong> {profile.email}
        </Typography>
        <Typography variant="body2">
          <strong>Phone:</strong> {profile.phone}
        </Typography>
        {profile.linkedinUrl && (
          <Typography variant="body2">
            <strong>LinkedIn:</strong> {profile.linkedinUrl}
          </Typography>
        )}
        {profile.personalWebsite && (
          <Typography variant="body2">
            <strong>Website:</strong> {profile.personalWebsite}
          </Typography>
        )}
      </Box>
      
      <Box>
        <Button 
          component={Link} 
          to="/profile/edit" 
          variant="outlined" 
          size="small"
        >
          Edit Profile
        </Button>
      </Box>
    </Paper>
  );
};

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const profileData = await userService.getProfile();
        setProfile(profileData);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        setError('Failed to load your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        My Profile
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your professional profile information
      </Typography>

      <ProfileCard profile={profile} />
    </Container>
  );
};

export default Profile; 